


































































































import Vue from 'vue';
import Loader from '@/components/util/Loader.vue';
import ItemCard from '@/components/item/DvItemCard.vue';
import UiString from '@/components/uistring/UiString.vue';
import CashShopEntryCard from './CashShopEntryCard.vue';
import CashShopProvider from '@/api/CashShopProvider';
import ICashShopEntry, { ICashShopTab } from '@/models/items/ICashShop';
import { categoryToTabNum } from '@/util/cashShop';

interface IData {
    loading: boolean;
    entry: ICashShopEntry | null;
    packageContents: ICashShopEntry[];
    variants: ICashShopEntry[];
    error: boolean;
}

export default Vue.extend({
    components: {
        Loader,
        ItemCard,
        UiString,
        CashShopEntryCard,
    },
    props: {
        sn: {
            type: Number,
            required: true,
        },
        tabs: {
            type: Array as () => ICashShopTab[],
            default: () => [],
        },
    },
    data(): IData {
        return {
            loading: true,
            entry: null,
            packageContents: [],
            variants: [],
            error: false,
        };
    },
    computed: {
        techMode(): boolean {
            return this.$store.state.techMode;
        },
        //  Members with price 0 are package-only; the savings math counts only
        //  the individually purchasable ones, so it under- rather than overstates.
        packageTotal(): number {
            return this.packageContents.reduce((sum, p) => sum + p.price, 0);
        },
        packageSavings(): number {
            return this.entry ? this.packageTotal - this.entry.price : 0;
        },
        packageSavingsPercent(): number {
            return this.packageTotal > 0 ? Math.round((this.packageSavings / this.packageTotal) * 100) : 0;
        },
        origin(): string {
            if (!this.entry) {
                return '';
            }

            const tabNum = categoryToTabNum(this.entry.category, this.entry.subCategory, this.tabs);
            if (tabNum === null) {
                return '';
            }

            const tab = this.tabs.find((t) => t.mtNum === tabNum);
            if (!tab) {
                return '';
            }

            const sub = tab.subTabs.find((s) => s.stNum === this.entry!.subCategory);
            return sub ? `${tab.name} › ${sub.name}` : tab.name;
        },
    },
    created() {
        this.load();
    },
    watch: {
        sn() {
            this.load();
        },
        '$store.state.regionCode'() {
            this.load();
        },
    },
    methods: {
        async load() {
            const reqSn = this.sn;
            //  On a switch (a panel already showing an entry), keep the current
            //  content visible and fetch the next one in the background, so the
            //  content crossfades in place instead of flashing the loading
            //  spinner. Only a cold open shows the spinner.
            const silent = this.entry !== null;
            if (!silent) {
                this.loading = true;
                this.entry = null;
                this.packageContents = [];
                this.variants = [];
            }
            this.error = false;
            try {
                const entry = await CashShopProvider.getEntry(reqSn);
                //  A newer switch superseded this request; drop the stale result.
                if (this.sn !== reqSn) return;
                let packageContents: ICashShopEntry[] = [];
                let variants: ICashShopEntry[] = [];
                if (entry) {
                    [packageContents, variants] = await Promise.all([
                        entry.isPackage
                            ? CashShopProvider.getPackageMemberSns(entry.sn).then((sns) => CashShopProvider.getEntriesBySns(sns))
                            : Promise.resolve([] as ICashShopEntry[]),
                        CashShopProvider.getEntriesBySns(entry.linkSns),
                    ]);
                    if (this.sn !== reqSn) return;
                }
                this.entry = entry;
                this.packageContents = packageContents;
                this.variants = variants;
            } catch {
                //  Cold open shows the error state; a silent switch keeps the
                //  previous entry rather than blanking the panel.
                if (this.sn === reqSn && !silent) {
                    this.error = true;
                }
            } finally {
                if (this.sn === reqSn) {
                    this.loading = false;
                }
            }
        },
    },
});
